import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import * as Tone from "tone";
import Slider from "./Slider";
import { Box, Button, Tip } from "grommet";

const gainToDb = (gain) => (gain <= 0 ? -Infinity : Tone.gainToDb(gain));
const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);

// Use forwardRef to allow the parent to access internal methods.
const Kick = React.forwardRef((props, ref) => {
  const [isKeyPressed, setIsKeyPressed] = useState(false);
  const [pitch, setPitch] = useState(4);
  const [length, setLength] = useState(0.05);
  const [decay, setDecay] = useState(0.64);
  const [speed, setSpeed] = useState(0.65);
  const [punch, setPunch] = useState(0.64);
  const [level, setLevel] = useState(0.8);

  const synth = useRef(null);
  const subSine = useRef(null);
  const punchSynth = useRef(null);
  const punchFilter = useRef(null);

  const configureSynth = useCallback(
    (kickSynth) => {
      const decaySeconds = Math.max(0.02, decay);
      const tailSeconds = Math.max(0.03, length);
      const decayIntensity = Math.min(Math.pow(decaySeconds, 0.45), 1);
      const punchAmount = clamp(punch);
      const speedAmount = clamp(speed);
      const levelAmount = clamp(level);
      const speedScale = 1.9 - speedAmount * 1.65;
      const pitchEnvelopeSeconds = Math.max(
        0.004,
        (0.01 + decayIntensity * 0.18 + punchAmount * 0.015) * speedScale,
      );

      kickSynth.set({
        pitchDecay: pitchEnvelopeSeconds,
        octaves: 4.8 + decayIntensity * 7.8 + punchAmount * 0.8,
        envelope: {
          attack: 0.0008,
          decay: decaySeconds,
          sustain: 0.005 + decayIntensity * 0.045,
          release: tailSeconds,
        },
      });
      kickSynth.volume.value = gainToDb(levelAmount);
    },
    [decay, length, level, punch, speed],
  );

  const prepareSynth = useCallback(() => {
    if (!synth.current) {
      if (typeof AudioBuffer === "undefined") {
        return false;
      }

      synth.current = new Tone.PolySynth({
        voice: Tone.MembraneSynth,
        maxPolyphony: 8,
      }).toDestination();
    }

    if (!subSine.current) {
      subSine.current = new Tone.Synth({
        oscillator: {
          type: "sine",
        },
        envelope: {
          attack: 0.002,
          decay: 0.25,
          sustain: 0.18,
          release: 0.18,
        },
      }).toDestination();
    }

    if (!punchFilter.current) {
      punchFilter.current = new Tone.Filter({
        frequency: 3200,
        type: "highpass",
        Q: 0.8,
      }).toDestination();
    }

    if (!punchSynth.current) {
      punchSynth.current = new Tone.NoiseSynth({
        noise: {
          type: "white",
        },
        envelope: {
          attack: 0.0001,
          decay: 0.006,
          sustain: 0,
          release: 0.0015,
        },
      }).connect(punchFilter.current);
    }

    configureSynth(synth.current);
    return true;
  }, [configureSynth]);

  useEffect(() => {
    if (synth.current) {
      configureSynth(synth.current);
    }
  }, [configureSynth]);

  useEffect(() => {
    return () => {
      synth.current?.dispose();
      subSine.current?.dispose();
      punchSynth.current?.dispose();
      punchFilter.current?.dispose();
      synth.current = null;
      subSine.current = null;
      punchSynth.current = null;
      punchFilter.current = null;
    };
  }, []);

  const resumeAudio = useCallback(async () => {
    if (Tone.context.state === "running") {
      return true;
    }
    try {
      await Tone.start();
      return Tone.context.state === "running";
    } catch (error) {
      console.warn("AudioContext start was blocked:", error);
      return false;
    }
  }, []);

  const playSynth = useCallback(
    (time, overrides = {}) => {
      if (Tone.context.state !== "running" || !prepareSynth()) {
        return;
      }

      const activePitch = overrides.pitch ?? pitch;
      const levelAmount = clamp(level);
      const punchAmount = clamp(punch);
      const playbackPitch = Math.max(activePitch, 1);
      const lowPitchWeight = 1 - clamp(activePitch / 25);
      const bodyDuration = Math.min(
        Math.max(0.045, length * 0.38 + decay * 0.12),
        0.42,
      );

      synth.current.triggerAttackRelease(playbackPitch, bodyDuration, time);
      if (level > 0) {
        const subDuration = Math.max(
          length + decay * (0.34 + lowPitchWeight * 0.18),
          0.12,
        );
        const subLevel = Math.min(
          levelAmount * (0.48 + decay * 0.08 + lowPitchWeight * 0.25),
          0.95,
        );
        subSine.current.envelope.decay = Math.max(decay * 0.42, 0.06);
        subSine.current.envelope.sustain = 0.06 + lowPitchWeight * 0.14;
        subSine.current.envelope.release = Math.max(length * 0.75, 0.06);
        subSine.current.volume.value = gainToDb(subLevel);
        subSine.current.triggerAttackRelease(
          Math.max(playbackPitch * (0.9 - lowPitchWeight * 0.08), 30),
          subDuration,
          time,
        );
      }
      if (punchAmount > 0) {
        punchFilter.current.frequency.value = 1800 + punchAmount * 5200;
        punchFilter.current.Q.value = 0.5 + punchAmount * 1.5;
        punchSynth.current.volume.value = gainToDb(
          levelAmount * punchAmount * 0.42,
        );
        punchSynth.current.envelope.decay = 0.003 + punchAmount * 0.007;
        punchSynth.current.triggerAttackRelease(
          0.004 + punchAmount * 0.008,
          time,
        );
      }
    },
    [decay, length, level, pitch, punch, prepareSynth],
  );

  const triggerFromUI = useCallback(async () => {
    if (!prepareSynth()) {
      return;
    }

    const canPlay = await resumeAudio();
    if (!canPlay) {
      return;
    }
    playSynth();
  }, [prepareSynth, resumeAudio, playSynth]);

  // Expose playSynth to the parent component via the ref.
  useImperativeHandle(ref, () => ({
    playSynth,
    prepareSynth,
  }));

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "KeyA" && !isKeyPressed) {
        setIsKeyPressed(true);
        triggerFromUI();
      }
    };
    const handleKeyUp = (event) => {
      if (event.code === "KeyA") {
        setIsKeyPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isKeyPressed, triggerFromUI]);

  return (
    <Box
      align="center"
      className="instrument-panel"
      pad="small"
      responsive={false}
    >
      <Tip
        dropProps={{ align: { left: "right" } }}
        content={"Trigger with A"}
        plain
        pad="small"
      >
        <Button
          className="instrument-trigger"
          label="Kick"
          onClick={triggerFromUI}
          primary
          size="small"
        />
      </Tip>
      <Slider
        parameter={pitch}
        setParameter={setPitch}
        minValue={0}
        maxValue={35}
        stepValue={1}
        controlName="Pitch"
        scale="exponential"
      />
      <Slider
        parameter={decay}
        setParameter={setDecay}
        minValue={0}
        maxValue={1}
        stepValue={0.01}
        controlName="Decay"
        scale="exponential"
      />
      <Slider
        parameter={speed}
        setParameter={setSpeed}
        minValue={0}
        maxValue={1}
        stepValue={0.01}
        controlName="Speed"
      />
      <Slider
        parameter={length}
        setParameter={setLength}
        minValue={0}
        maxValue={1.5}
        stepValue={0.01}
        controlName="Length"
        scale="exponential"
      />
      <Slider
        parameter={punch}
        setParameter={setPunch}
        minValue={0}
        maxValue={1}
        stepValue={0.01}
        controlName="Punch"
      />
      <Slider
        compact
        parameter={level}
        setParameter={setLevel}
        minValue={0}
        maxValue={1}
        stepValue={0.01}
        controlName="Level"
      />
    </Box>
  );
});

export default React.memo(Kick);
